import { inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import { map, Observable, of, take, tap } from 'rxjs';

import { UserDetailsFacade } from '@shared/state/facades';
import { RoutingService } from '@shared/services';
import { DprDetailsRouteData } from '@dpr-details/models';
import { CanActivateFn } from '@shared/models/guard';

export const canActivateTabGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot
): Observable<boolean> => {
  const routingService = inject(RoutingService);
  const userDetailsFacade = inject(UserDetailsFacade);

  return canActivateTab(route, routingService, userDetailsFacade);
};

export const canActivateTab = (
  route: ActivatedRouteSnapshot,
  routingService: RoutingService,
  userDetailsFacade: UserDetailsFacade
): Observable<boolean> => {
  const routeData = route.data as DprDetailsRouteData;

  // if no access condition is defined -> access allowed
  if (!routeData.canAccess) {
    return of(true);
  }

  // if accessCondition is function (for dynamic tabs) compute the function result
  // else execute check if the name of the tab is enabled
  const accessCondition =
    typeof routeData.canAccess === 'function'
      ? routeData.canAccess(userDetailsFacade, route)
      : canAccessPredefinedTab(userDetailsFacade, routeData.canAccess);

  return accessCondition.pipe(
    tap((result) => {
      // if the user is not allowed to access the tab redirect to the first dpr-details route
      if (!result) {
        routingService.navigateToRoute(
          routingService.routePrefix,
          'dpr-details',
          routingService.subprojectIdFromCurrentRoute,
          routingService.dprDetailIdFromCurrentRoute
        );
      }
    })
  );
};

const canAccessPredefinedTab = (
  userDetailsFacade: UserDetailsFacade,
  tabEnableProp: string
): Observable<boolean> => {
  return userDetailsFacade.query.subprojectConfiguration$.pipe(
    take(1),
    map((subprojectConfiguration) => {
      if (!subprojectConfiguration[tabEnableProp]) {
        return false;
      }

      return true;
    })
  );
};
